/**
 * Company Risk Narrative Component
 *
 * Displays AI-generated risk narrative for a company.
 * Shows cached narrative by default - only regenerates on explicit action.
 *
 * Key UX requirements:
 * - CRITICAL: Never regenerate on page load (uses AI credits)
 * - Show cached narrative with timestamp
 * - "Regenerate" button with confirmation
 * - Empty state prompts first generation
 */

import React, { useState } from 'react';
import {
  Sparkles,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Clock,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { useCompanyRiskNarrative, useRegenerateCompanyRiskNarrative } from '../../hooks/useAI';

const RISK_LEVEL_CONFIG = {
  low: {
    label: 'Low Risk',
    color: '#22c55e',
    bg: 'rgba(34, 197, 94, 0.1)',
    icon: CheckCircle,
  },
  medium: {
    label: 'Medium Risk',
    color: '#f59e0b',
    bg: 'rgba(245, 158, 11, 0.1)',
    icon: AlertCircle,
  },
  high: {
    label: 'High Risk',
    color: '#ef4444',
    bg: 'rgba(239, 68, 68, 0.1)',
    icon: AlertTriangle,
  },
  critical: {
    label: 'Critical Risk',
    color: '#dc2626',
    bg: 'rgba(220, 38, 38, 0.15)',
    icon: XCircle,
  },
};

function formatRelativeTime(dateString) {
  if (!dateString) return '';

  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins === 1 ? '' : 's'} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
  return date.toLocaleDateString();
}

export default function CompanyRiskNarrative({ companyId }) {
  const [showConfirm, setShowConfirm] = useState(false);
  const [findingsExpanded, setFindingsExpanded] = useState(true);
  const [recommendationsExpanded, setRecommendationsExpanded] = useState(true);

  const { data: narrative, isLoading, error } = useCompanyRiskNarrative(companyId, {
    // Don't auto-generate on fetch - only return cached
    retry: false,
  });
  const regenerateMutation = useRegenerateCompanyRiskNarrative();

  const handleRegenerate = async () => {
    setShowConfirm(false);
    try {
      await regenerateMutation.mutateAsync(companyId);
    } catch (err) {
      console.error('Failed to regenerate narrative:', err);
    }
  };

  const riskConfig = narrative?.risk_level
    ? RISK_LEVEL_CONFIG[narrative.risk_level] || RISK_LEVEL_CONFIG.medium
    : null;
  const RiskIcon = riskConfig?.icon || AlertCircle;

  // Empty state - no narrative generated yet
  const isEmpty = !narrative?.narrative && !isLoading && !error;

  return (
    <div className="risk-narrative">
      <style>{`
        .risk-narrative {
          background: var(--bg-secondary, #111318);
          border: 1px solid var(--border-primary, #23262f);
          border-radius: 12px;
          overflow: hidden;
        }

        .narrative-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px 20px;
          border-bottom: 1px solid var(--border-primary, #23262f);
          background: linear-gradient(135deg, rgba(99, 102, 241, 0.05) 0%, transparent 50%);
        }

        .narrative-title {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 14px;
          font-weight: 600;
          color: var(--text-primary, #f0f2f5);
          margin: 0;
        }

        .narrative-title svg {
          width: 18px;
          height: 18px;
          color: var(--accent-primary, #6366f1);
        }

        .narrative-meta {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .narrative-timestamp {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          color: var(--text-tertiary, #64748b);
        }

        .narrative-timestamp svg {
          width: 14px;
          height: 14px;
        }

        .regenerate-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          background: transparent;
          border: 1px solid var(--border-primary, #23262f);
          border-radius: 6px;
          color: var(--text-secondary, #8b919e);
          font-size: 12px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.15s;
          font-family: inherit;
        }

        .regenerate-btn:hover:not(:disabled) {
          background: var(--bg-tertiary, #1a1d24);
          color: var(--text-primary, #f0f2f5);
        }

        .regenerate-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .regenerate-btn svg {
          width: 14px;
          height: 14px;
        }

        .regenerate-btn.spinning svg {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .narrative-body {
          padding: 20px;
        }

        .risk-badge-large {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 10px 16px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          margin-bottom: 16px;
        }

        .risk-badge-large svg {
          width: 18px;
          height: 18px;
        }

        .narrative-text {
          font-size: 14px;
          line-height: 1.8;
          color: var(--text-secondary, #8b919e);
          white-space: pre-wrap;
          margin-bottom: 20px;
        }

        .narrative-section {
          margin-bottom: 16px;
        }

        .narrative-section:last-child {
          margin-bottom: 0;
        }

        .section-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 10px 0;
          cursor: pointer;
          user-select: none;
        }

        .section-header:hover .section-title {
          color: var(--text-primary, #f0f2f5);
        }

        .section-title {
          font-size: 13px;
          font-weight: 600;
          color: var(--text-secondary, #8b919e);
          text-transform: uppercase;
          letter-spacing: 0.05em;
          transition: color 0.15s;
        }

        .section-header svg {
          width: 16px;
          height: 16px;
          color: var(--text-tertiary, #64748b);
        }

        .section-content {
          padding-left: 4px;
        }

        .finding-list, .recommendation-list {
          list-style: none;
          margin: 0;
          padding: 0;
        }

        .finding-item, .recommendation-item {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          padding: 8px 0;
          font-size: 13px;
          color: var(--text-secondary, #8b919e);
          line-height: 1.5;
        }

        .finding-item::before {
          content: "";
          width: 6px;
          height: 6px;
          background: #f59e0b;
          border-radius: 50%;
          margin-top: 6px;
          flex-shrink: 0;
        }

        .recommendation-item::before {
          content: "";
          width: 6px;
          height: 6px;
          background: var(--accent-primary, #6366f1);
          border-radius: 50%;
          margin-top: 6px;
          flex-shrink: 0;
        }

        /* Empty State */
        .empty-state {
          padding: 40px 20px;
          text-align: center;
        }

        .empty-icon {
          width: 48px;
          height: 48px;
          color: var(--text-tertiary, #64748b);
          margin-bottom: 16px;
        }

        .empty-title {
          font-size: 16px;
          font-weight: 600;
          color: var(--text-primary, #f0f2f5);
          margin: 0 0 8px;
        }

        .empty-description {
          font-size: 14px;
          color: var(--text-secondary, #8b919e);
          margin: 0 0 20px;
          max-width: 400px;
          margin-left: auto;
          margin-right: auto;
        }

        .generate-btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 10px 20px;
          background: var(--accent-primary, #6366f1);
          border: none;
          border-radius: 8px;
          color: white;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.15s;
          font-family: inherit;
        }

        .generate-btn:hover:not(:disabled) {
          background: var(--accent-hover, #5558e3);
        }

        .generate-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .generate-btn svg {
          width: 18px;
          height: 18px;
        }

        /* Confirm Dialog */
        .confirm-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.6);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10000;
          animation: fadeIn 0.15s ease-out;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .confirm-dialog {
          background: var(--bg-primary, #0a0b0e);
          border: 1px solid var(--border-primary, #23262f);
          border-radius: 12px;
          padding: 24px;
          max-width: 400px;
          width: 100%;
          margin: 20px;
          animation: scaleIn 0.15s ease-out;
        }

        @keyframes scaleIn {
          from { transform: scale(0.95); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }

        .confirm-title {
          font-size: 16px;
          font-weight: 600;
          color: var(--text-primary, #f0f2f5);
          margin: 0 0 12px;
        }

        .confirm-message {
          font-size: 14px;
          color: var(--text-secondary, #8b919e);
          margin: 0 0 20px;
          line-height: 1.6;
        }

        .confirm-warning {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          padding: 12px;
          background: rgba(245, 158, 11, 0.08);
          border: 1px solid rgba(245, 158, 11, 0.2);
          border-radius: 8px;
          margin-bottom: 20px;
          font-size: 12px;
          color: #f59e0b;
          line-height: 1.5;
        }

        .confirm-warning svg {
          width: 16px;
          height: 16px;
          flex-shrink: 0;
          margin-top: 1px;
        }

        .confirm-actions {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
        }

        .confirm-btn {
          padding: 8px 16px;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.15s;
          font-family: inherit;
        }

        .confirm-btn-cancel {
          background: var(--bg-secondary, #111318);
          border: 1px solid var(--border-primary, #23262f);
          color: var(--text-primary, #f0f2f5);
        }

        .confirm-btn-cancel:hover {
          background: var(--bg-tertiary, #1a1d24);
        }

        .confirm-btn-confirm {
          background: var(--accent-primary, #6366f1);
          border: none;
          color: white;
        }

        .confirm-btn-confirm:hover {
          background: var(--accent-hover, #5558e3);
        }

        /* Loading State */
        .loading-state {
          padding: 40px 20px;
          text-align: center;
          color: var(--text-secondary, #8b919e);
        }

        .loading-spinner {
          width: 24px;
          height: 24px;
          border: 2px solid var(--border-primary, #23262f);
          border-top-color: var(--accent-primary, #6366f1);
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
          margin: 0 auto 12px;
        }

        /* Error State */
        .error-state {
          padding: 20px;
          background: rgba(239, 68, 68, 0.08);
          border-radius: 8px;
          color: #ef4444;
          font-size: 13px;
        }
      `}</style>

      <div className="narrative-header">
        <h3 className="narrative-title">
          <Sparkles />
          AI Risk Narrative
        </h3>
        {narrative?.narrative && (
          <div className="narrative-meta">
            {narrative.generated_at && (
              <span className="narrative-timestamp">
                <Clock />
                Generated {formatRelativeTime(narrative.generated_at)}
              </span>
            )}
            <button
              className={`regenerate-btn ${regenerateMutation.isPending ? 'spinning' : ''}`}
              onClick={() => setShowConfirm(true)}
              disabled={regenerateMutation.isPending}
            >
              <RefreshCw />
              {regenerateMutation.isPending ? 'Generating...' : 'Regenerate'}
            </button>
          </div>
        )}
      </div>

      {isLoading && (
        <div className="loading-state">
          <div className="loading-spinner" />
          Loading narrative...
        </div>
      )}

      {error && (
        <div className="narrative-body">
          <div className="error-state">
            Failed to load risk narrative. The narrative may not have been generated yet.
          </div>
        </div>
      )}

      {isEmpty && (
        <div className="empty-state">
          <Sparkles className="empty-icon" />
          <h4 className="empty-title">No Risk Narrative Yet</h4>
          <p className="empty-description">
            Generate an AI-powered risk narrative to get a plain-English assessment
            of this company's compliance risk profile.
          </p>
          <button
            className="generate-btn"
            onClick={() => regenerateMutation.mutate(companyId)}
            disabled={regenerateMutation.isPending}
          >
            <Sparkles />
            {regenerateMutation.isPending ? 'Generating...' : 'Generate Risk Narrative'}
          </button>
        </div>
      )}

      {narrative?.narrative && (
        <div className="narrative-body">
          {riskConfig && (
            <div
              className="risk-badge-large"
              style={{ color: riskConfig.color, background: riskConfig.bg }}
            >
              <RiskIcon />
              {riskConfig.label}
            </div>
          )}

          <div className="narrative-text">{narrative.narrative}</div>

          {narrative.key_findings?.length > 0 && (
            <div className="narrative-section">
              <div
                className="section-header"
                onClick={() => setFindingsExpanded(!findingsExpanded)}
              >
                <span className="section-title">Key Findings ({narrative.key_findings.length})</span>
                {findingsExpanded ? <ChevronUp /> : <ChevronDown />}
              </div>
              {findingsExpanded && (
                <div className="section-content">
                  <ul className="finding-list">
                    {narrative.key_findings.map((finding, i) => (
                      <li key={i} className="finding-item">{finding}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {narrative.recommendations?.length > 0 && (
            <div className="narrative-section">
              <div
                className="section-header"
                onClick={() => setRecommendationsExpanded(!recommendationsExpanded)}
              >
                <span className="section-title">Recommendations ({narrative.recommendations.length})</span>
                {recommendationsExpanded ? <ChevronUp /> : <ChevronDown />}
              </div>
              {recommendationsExpanded && (
                <div className="section-content">
                  <ul className="recommendation-list">
                    {narrative.recommendations.map((rec, i) => (
                      <li key={i} className="recommendation-item">{rec}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Confirmation Dialog */}
      {showConfirm && (
        <div className="confirm-overlay" onClick={() => setShowConfirm(false)}>
          <div className="confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <h4 className="confirm-title">Regenerate Risk Narrative?</h4>
            <p className="confirm-message">
              This will generate a new AI risk narrative for this company,
              replacing the existing one.
            </p>
            <div className="confirm-warning">
              <AlertTriangle />
              <span>
                This action uses AI credits. Only regenerate if significant new
                information has been added (UBOs, screening results, documents).
              </span>
            </div>
            <div className="confirm-actions">
              <button className="confirm-btn confirm-btn-cancel" onClick={() => setShowConfirm(false)}>
                Cancel
              </button>
              <button className="confirm-btn confirm-btn-confirm" onClick={handleRegenerate}>
                Regenerate
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
